/* The "what changed since last run" FEED (GET /clients/{slug}/changes/summary).
 * The small sibling of api/changes.ts: where /changes returns the FULL
 * coverage-honest structural delta (the Activity tab), /changes/summary returns
 * a closed-vocabulary feed of a handful of severity-toned, provenance-stamped
 * one-liners — the Overview §1 morning brief. Deterministic, $0, no model call. */
import { request } from './client.ts';
import type { RunMeta } from './changes.ts';

/** The closed entry-type vocabulary (mirrors tools/change_summary.ENTRY_TYPES). */
export type ChangeEntryType =
  | 'score.moved'
  | 'finding.entered' | 'finding.left' | 'finding.reranked'
  | 'assets.appeared' | 'assets.disappeared'
  | 'plane.measured' | 'plane.unmeasured'
  | 'ai.ownership' | 'ai.topDomain' | 'ai.engines'
  | 'entity.added' | 'entity.removed' | 'entity.leadership'
  | 'linkgraph.orphans' | 'linkgraph.bridges'
  | 'none.material';

export type ChangeSeverity = 'info' | 'notable' | 'attention';

export interface ChangeEntry {
  type: ChangeEntryType;
  severity: ChangeSeverity;
  text: string;
  /** the two run dates + the payload path that moved (zone-5 provenance). */
  provenance: { from: string | null; to: string | null; path: string };
}

export interface ChangesSummaryResponse {
  /** true when the client has zero or exactly one run — nothing to compare yet. */
  firstRun: boolean;
  comparable: boolean;
  reason?: string;
  slug?: string;
  from?: RunMeta;
  to?: RunMeta;
  /** YYYY-MM-DD calendar dates of the two runs (provenance footer). */
  fromDate?: string | null;
  toDate?: string | null;
  entries: ChangeEntry[];
  entryCount?: number;
  truncated?: boolean;
  notComparable?: { layer: string; reason: string }[];
}

/** GET /clients/{slug}/changes/summary — the small typed what-changed feed. */
export function getChangesSummary(slug: string): Promise<ChangesSummaryResponse> {
  return request<ChangesSummaryResponse>(`/clients/${encodeURIComponent(slug)}/changes/summary`);
}
